<template>
  <div class="month_calendar">
    <div class="month_control">
      <button v-if="uiType !== 'right'" type="button" class="prev"
        :class="{ disabled: !enablePrevMonth }"
        @click="addMonthStr(-1)">
        <i class="icon icon--calendar-month"></i>
      </button>
      <p class="month_label">{{ monthLabel }}</p>
      <button v-if="uiType !== 'left'" type="button" class="next"
        :class="{ disabled: !enableNextMonth }"
        @click="addMonthStr(1)">
        <i class="icon icon--calendar-month"></i>
      </button>
    </div>
    <table class="date_grid" :class="calendarType">
      <th v-for="dayName in dayNames" :key="dayName">
        <span>{{ dayName }}</span>
      </th>
      <tr v-for="(week, weekIndex) in dateList" :key="weekIndex">
        <td v-for="(item, index) in week" :key="index"
          :class="item.cssClass"
          @click="onDateClick(item)"
          @mouseenter="onDateMouseEnter(item)">
          <span>{{ item.date.getDate() }}</span>
        </td>
      </tr>
    </table>
  </div>
</template>

<script>
import {
  parse,
  format,
  startOfMonth,
  lastDayOfMonth,
  addDays,
  addMonths,
  isBefore,
  isWithinRange,
  isSameDay,
  startOfDay,
  endOfDay
} from 'date-fns'
import { partition, isInRanges } from '@/libs/utils'

const DAYS_IN_WEEK = 7
const DAY_OF_SATURDAY = 6
const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export default {
  name: 'MonthCalendar',
  props: {
    uiType: {
      type: String,
      default: 'single'
    },
    monthStr: {
      type: String,
      required: true
    },
    targetDate: {
      type: Date,
      required: false
    },
    minDate: {
      type: Date,
      required: false
    },
    maxDate: {
      type: Date,
      required: false
    },
    minSelectableDate: {
      type: Date,
      required: false
    },
    dateRange: {
      type: Object,
      required: false
    },
    previewEndDate: {
      required: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    highlight: {
      default: false
    },
    unpaid: {
      default: false
    },
    showPaid: {
      default: false
    },
    analysisRange: {
      type: Object,
      default: () => {
        return null
      }
    }
  },
  data () {
    return {}
  },
  computed: {
    monthLabel () {
      return format(this.monthDate, 'MMM, YYYY')
    },
    monthDate () {
      let date = parse(this.monthStr)

      if (this.uiType === 'left') {
        date = addMonths(date, -1)
      }
      return parse(date)
    },
    dateList () {
      let items = []
      const startDate = startOfMonth(this.monthDate)
      const endDate = lastDayOfMonth(this.monthDate)
      const startDayOfWeek = startDate.getDay()
      const endDayOfWeek = endDate.getDay()

      const startDateOffset = -startDayOfWeek
      let endDateOffset = endDate.getDate() - 1 + (DAY_OF_SATURDAY - endDayOfWeek)

      if (this.uiType !== 'single') {
        const dateSize = endDateOffset - startDateOffset + 1
        const weekSize = dateSize / DAYS_IN_WEEK
        const WEEKS_TO_DISPLAY = 6

        if (weekSize < WEEKS_TO_DISPLAY) {
          const daysToAdd = (WEEKS_TO_DISPLAY - weekSize) * DAYS_IN_WEEK
          endDateOffset += daysToAdd
        }
      }

      for (let i = startDateOffset; i <= endDateOffset; i++) {
        const date = addDays(startDate, i)
        items.push({
          date,
          cssClass: this.getDateClass(date)
        })
      }

      items = partition(items, DAYS_IN_WEEK)
      return items
    },
    enableNextMonth () {
      if (this.disabled) {
        return false
      }

      let result = true

      if (this.maxDate) {
        const endDate = lastDayOfMonth(this.monthDate)
        result = isBefore(endDate, this.maxDate)
      }
      return result
    },
    enablePrevMonth () {
      if (this.disabled) {
        return false
      }

      let result = true

      if (this.minDate) {
        const startDate = startOfMonth(this.monthDate)
        result = isBefore(this.minDate, startDate)
      }
      return result
    },
    calendarType () {
      return this.analysisRange ? 'analysis' : 'journey'
    }
  },
  created () {
    this.dayNames = dayNames
  },
  methods: {
    getDateClass (date) {
      const range = this.dateRange
      let isInMonth = date.getMonth() === this.monthDate.getMonth()
      let isToday = false
      let isDisabled = false
      let isInRange = false
      let isLastDay = false
      let isStartDate = false
      let isEndDate = false
      let isTargetDate = false
      let isMonthStartDay = false
      let isMonthLastDay = false
      let isWeekStartDay = false
      let isWeekLastDay = false
      let isHighligted = false
      let isEveryone = false
      let isAnalysis = false

      if (isSameDay(date, new Date())) {
        isToday = true
      }

      if (this.targetDate && isSameDay(date, this.targetDate)) {
        isTargetDate = true
      }

      if (this.minDate && isBefore(date, startOfDay(this.minDate))) {
        isDisabled = true
      }

      if (this.maxDate && isBefore(endOfDay(this.maxDate), date)) {
        isDisabled = true
      }

      if (this.minSelectableDate && isBefore(date, startOfDay(this.minSelectableDate))) {
        isDisabled = true
      }

      if (range) {
        const previewEndDate = range.endDate || this.previewEndDate

        if (range.startDate && previewEndDate &&
          isWithinRange(date, range.startDate, previewEndDate)) {
          isInRange = true

          if (this.previewEndDate && isSameDay(date, this.previewEndDate)) {
            isLastDay = true
          }
        }

        if (range.startDate && isSameDay(date, range.startDate)) {
          isStartDate = true
        }
        if (range.endDate && isSameDay(date, range.endDate)) {
          isEndDate = true
        }
        if (range.startDate && format(date, 'YYYY-MM-DD') > format(addMonths(range.startDate, 6), 'YYYY-MM-DD')) {
          isDisabled = true
        }
      }
      if (this.analysisRange) {
        if (isWithinRange(date, this.analysisRange.startDate, this.analysisRange.endDate)) {
          isAnalysis = true
        }
      }

      if (isSameDay(date, startOfMonth(this.monthDate))) {
        isMonthStartDay = true
      }

      if (isSameDay(date, lastDayOfMonth(this.monthDate))) {
        isMonthLastDay = true
      }

      if (date.getDay() === 0) {
        isWeekStartDay = true
      }

      if (date.getDay() === 6) {
        isWeekLastDay = true
      }
      if (this.highlight && isInRanges(date, this.highlight)) {
        isHighligted = true
      }

      if (this.showPaid) {
        if (this.unpaid && isInRanges(date, this.unpaid)) {
          if (format(date, 'YYYY-MM-DD') <= format(new Date(), 'YYYY-MM-DD')) {
            isEveryone = true
          }
        }
      }

      if (!isHighligted && (!isEveryone && this.showPaid)) {
        isDisabled = true
      }

      let isActive = isStartDate || isEndDate

      return {
        off: !isInMonth || isDisabled || this.disabled,
        today: isToday,
        unavailable: !isDisabled && !this.disabled,
        disabled: isDisabled || this.disabled,
        active: isActive,
        highlight: isHighligted,
        everyone: isEveryone,
        target: isTargetDate,
        'not-in-month': !isInMonth,
        'in-range': isInRange,
        'last-day': isLastDay,
        'start-date': isStartDate,
        'end-date': isEndDate,
        'month-start-day': isMonthStartDay,
        'month-last-day': isMonthLastDay,
        'week-start-day': isWeekStartDay,
        'week-last-day': isWeekLastDay,
        'analysis-day': isAnalysis
      }
    },
    addMonthStr (months) {
      this.$emit('onMonthAdd', months)
    },
    onDateClick (item) {
      if (item.cssClass.disabled) {
        return
      }
      this.$emit('onDateClick', item.date, this.uiType)
    },
    onDateMouseEnter (item) {
      if (this.uiType === 'single' || item.cssClass.disabled) {
        return
      }
      if (this.dateRange.startDate && !this.dateRange.endDate &&
        isBefore(this.dateRange.startDate, item.date)) {
        this.$emit('onRangePreviewChange', this.uiType, item.date)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$analysisColor: #0085FF;
  .month_control {
    button {
      transition: background-color 0.2s ease;
      margin-top: -1px;
    }
  }

  .date_range_picker .month_calendar {
    td span {
      border: 1px solid transparent;
      transition: background-color 0.2s ease,
                  border-color 0.2s ease,
                  border-radius 0.3s ease;
      display: inline-block;
      box-sizing: border-box;
    }

    td.active:not(.off),
    td.active:not(.off):hover,
    td.in-range:not(.off),
    td.target:not(.off),

    td.in-range.last-day:not(.off) {
      span {
        border-top-color: #dddddd;
        border-bottom-color: #dddddd;
        // border-right-color: #dddddd;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-left-color: transparent;
        border-left: 0;
        border-right: 0;
      }
    }

    td.start-date {
      span {
        border-top-left-radius: 20px !important;
        border-bottom-left-radius: 20px !important;
        border-left: 1px solid #ddd !important;
      }
    }
    td.end-date {
      span {
        border-top-right-radius: 20px !important;
        border-bottom-right-radius: 20px !important;
        border-right: 1px solid #ddd !important;
      }
    }

    td.in-range:not(.off) {

      &.week-last-day,
      &.month-last-day {
        span {
          border-top-right-radius: 3px;
          border-bottom-right-radius: 3px;
          border-right: 1px solid #ddd !important;
        }
      }

      &.week-start-day,
      &.month-start-day {
        span {
          border-top-left-radius: 3px;
          border-bottom-left-radius: 3px;
          border-left: 1px solid #ddd !important;
        }
      }
    }

    td.not-in-month {
      visibility: hidden;
    }

    td {
      &.highlight {
        color: #FF2D92;
      }

      &.everyone {
      color: #333333;
      }

      &:hover {
        background-color: transparent;
      }
    }
    td.analysis-day span{
      color: $analysisColor !important;
    }
    .journey {
      td.active:not(.off),
      td.active:not(.off):hover,
      td.in-range:not(.off),
      td.target:not(.off),
      td.in-range.last-day:not(.off) {
        span {
          border-color: transparent !important;
        }
      }
      td.in-range.week-start-day,
      td.in-range.start-date {
        span {
          border-color: transparent !important;
        }
      }
    }
  }
</style>
